import React, { useState, useEffect, useCallback } from "react";
import {
  CircularProgress,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router-dom";
import { getAllRoutesUsers, becomeUser, deleteUser } from "./utils";

const RoutesUsersPage = () => {
  const [routesUsers, setRoutesUsers] = useState(null);
  const [authenticationChecked, setAuthenticationChecked] = useState("Success");
  const [confirmUser, setConfirmUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const navigate = useNavigate();

  const loadUsers = useCallback(() => {
    getAllRoutesUsers(setRoutesUsers, setAuthenticationChecked);
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleBecomeUser = async () => {
    if (!confirmUser) return;
    try {
      await becomeUser(confirmUser.id);
      setConfirmUser(null);

      // Deliberately keep the admin session state. becomeUser.json no longer logs the admin
      // out (gateway #28) -- it adds the target user's alertsLoggedIn cookie alongside the
      // admin's own loggedIn cookie, so both identities are live at once. Clearing isAdmin
      // here is what used to make the admin appear signed out.
      //
      // Set the trip-user state to the account being impersonated, the same shape the normal
      // trip login stores, so the header renders that user (UserProfile reads user.name) and
      // its logout control is available. That control posts to /user/trip/logout.json, which
      // clears only the alerts cookie -- ending the impersonation and leaving the admin
      // signed in.
      const impersonated = {
        id: confirmUser.id,
        name: confirmUser.email,
        email: confirmUser.email,
        impersonated: true,
      };
      sessionStorage.setItem("isUser", "true");
      sessionStorage.setItem("user", JSON.stringify(impersonated));
      localStorage.setItem("user", JSON.stringify(impersonated));
      localStorage.setItem("authToken", "true");
      window.dispatchEvent(new Event("userLoginChange"));

      navigate("/Routes/MyRoutes");
    } catch (err) {
      alert("Failed to become user: " + err.message);
      setConfirmUser(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirmUser) return;
    try {
      await deleteUser(deleteConfirmUser.id);
      setDeleteConfirmUser(null);
      loadUsers();
    } catch (err) {
      alert("Failed to delete user: " + err.message);
      setDeleteConfirmUser(null);
    }
  };

  const convertDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  const formatSuspendPeriod = (period) => {
    if (!period || !period.start) return "";
    const start = new Date(period.start).toLocaleDateString();
    const end = period.end ? new Date(period.end).toLocaleDateString() : "?";
    return `${start} – ${end}`;
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        <div style={{ flex: 1 }} />
        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    );
  }

  const dataGridColumns = [
    {
      field: "id",
      headerName: "Id",
      filterable: true,
      sortable: true,
      width: 70,
      renderCell: (params) => (
        <Link
          className="hyper-links"
          onClick={() => setConfirmUser(params.row)}
        >
          {params.row.id}
        </Link>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      filterable: true,
      sortable: true,
      width: 250,
    },
    {
      field: "format",
      headerName: "Format",
      filterable: true,
      sortable: true,
      width: 80,
    },
    {
      field: "routes",
      headerName: "Routes",
      filterable: true,
      sortable: true,
      width: 300,
      renderCell: (params) => (params.row.routes || []).join(", "),
    },
    {
      field: "verified",
      headerName: "Verified",
      filterable: true,
      sortable: true,
      width: 80,
      renderCell: (params) => (params.row.verified ? "Yes" : "No"),
    },
    {
      field: "bouncing",
      headerName: "Bouncing",
      filterable: true,
      sortable: true,
      width: 90,
      renderCell: (params) => (params.row.bouncing ? "Yes" : ""),
    },
    {
      field: "suspendPeriod",
      headerName: "Suspended",
      filterable: true,
      sortable: true,
      width: 180,
      renderCell: (params) => formatSuspendPeriod(params.row.suspendPeriod),
    },
    {
      field: "created",
      headerName: "Created",
      filterable: true,
      sortable: true,
      width: 150,
      renderCell: (params) => convertDate(params.row.created),
    },
    {
      field: "updated",
      headerName: "Updated",
      filterable: true,
      sortable: true,
      width: 150,
      renderCell: (params) => convertDate(params.row.updated),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Delete user">
          <IconButton
            size="small"
            onClick={() => setDeleteConfirmUser(params.row)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // User list view
  if (routesUsers !== null && routesUsers.length > 0) {
    return (
      <>
        <Typography variant="h5" sx={{ mb: 2, textAlign: "center", mt: 2 }}>
          Manage Routes User Accounts
        </Typography>
        <DataGrid
          rows={routesUsers}
          columns={dataGridColumns}
          disableSelectionOnClick
          components={{
            Toolbar: CustomToolbar,
          }}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "1rem",
              fontWeight: "bold",
              display: "flex",
            },
          }}
        />

        {/* Become user confirmation dialog */}
        <Dialog open={!!confirmUser} onClose={() => setConfirmUser(null)}>
          <DialogTitle>Login as User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Do you want to login as user <strong>{confirmUser?.email}</strong>
              ? You will stay logged in as an admin. Use the user logout to
              stop.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmUser(null)}>Cancel</Button>
            <Button
              onClick={handleBecomeUser}
              variant="contained"
              color="primary"
            >
              Yes, Login as User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete user confirmation dialog */}
        <Dialog
          open={!!deleteConfirmUser}
          onClose={() => setDeleteConfirmUser(null)}
        >
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete user{" "}
              <strong>{deleteConfirmUser?.email}</strong>? This will permanently
              remove their account and all saved routes.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmUser(null)}>Cancel</Button>
            <Button
              onClick={handleDeleteUser}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  } else {
    if (authenticationChecked === "Success") {
      return (
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <CircularProgress />
        </Box>
      );
    }
    if (authenticationChecked === "UNAUTHORIZED")
      return (
        <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
          You are not authorized to view this page or Session Expired.
          <br />
          <a
            href={process.env.PUBLIC_URL + "/About/AdminLogin"}
            className="hyper-links"
          >
            <b>Click here to login</b>
          </a>
        </Typography>
      );
    if (authenticationChecked === "FORBIDDEN")
      return (
        <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
          Access denied - You don't have the required permissions to view this
          page.
        </Typography>
      );
  }
};

export default RoutesUsersPage;
